import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { printHtml } from '../../../utils/printDocument';
import { appStylesheets, savePdf } from '../../../utils/pdfDocument';
import { clinicToday } from '../../../utils/clinicTime';
import ReportPrintLayout from './ReportPrintLayout';

// The letterhead copy of a report, printed in a clean window.
//
// A report page prints its ReportPrintLayout over itself with window.print(). A report's detail
// window cannot: it sits over a page that already holds that page's own print layout, and both
// would land on the paper. So the detail is laid out off the page, with the same component and
// therefore the same letterhead, and printed through printHtml like the order sheet and the
// prescription.

const STYLES = 'body { margin: 0; background: #fff; }';

/** ReportPrintLayout with these props, as markup. */
function reportHtml(props) {
  const host = document.createElement('div');
  const root = createRoot(host);
  flushSync(() => root.render(<ReportPrintLayout {...props} />));
  const bodyHtml = host.innerHTML;
  root.unmount();
  return bodyHtml;
}

/**
 * Prints ReportPrintLayout with these props.
 *
 * @returns {boolean} false when the browser blocked the print window, so the caller can say so
 */
export function printReport(props) {
  return printHtml({ title: props.title, styles: STYLES, bodyHtml: reportHtml(props) });
}

/** The same letterhead copy, saved as a PDF named for the report. */
export function saveReportPdf(props) {
  return savePdf({ title: props.title, fileName: props.title, styles: STYLES, bodyHtml: reportHtml(props) });
}

/**
 * The report on screen, saved as a PDF, from the Download PDF beside its Print. A report that prints
 * a letterhead copy (its ReportPrintLayout, hidden on screen) saves that. One that prints the page
 * itself saves the page as it prints: without the menus, the buttons, or anything marked to stay off
 * paper, laid out a little wider than the page and shrunk onto it.
 */
export function saveReportPagePdf(from) {
  const scope = from?.closest('#app-main') || document.getElementById('app-main') || document.body;
  const layout = scope.querySelector('[data-report-print]');
  if (layout) {
    const title = layout.getAttribute('data-report-print') || 'Report';
    return savePdf({ title, fileName: `${title} ${clinicToday()}`, styles: STYLES, bodyHtml: layout.outerHTML });
  }
  const copy = scope.cloneNode(true);
  // What has been typed or picked lives on the element, not in its markup, so a copy would lose it.
  const live = scope.querySelectorAll('input, textarea, select');
  copy.querySelectorAll('input, textarea, select').forEach((el, i) => {
    const was = live[i];
    if (!was) return;
    if (el.tagName === 'SELECT') Array.from(el.options).forEach((o, j) => o.toggleAttribute('selected', j === was.selectedIndex));
    else if (el.tagName === 'TEXTAREA') el.textContent = was.value;
    else if (el.type === 'checkbox' || el.type === 'radio') el.toggleAttribute('checked', was.checked);
    else el.setAttribute('value', was.value);
  });
  copy.querySelectorAll('.print\\:hidden, .no-print').forEach((el) => el.remove());
  const title = (copy.querySelector('h1, h2')?.textContent || 'Report').trim();
  return savePdf({ title, fileName: `${title} ${clinicToday()}`, headHtml: appStylesheets(), bodyHtml: copy.innerHTML, widthPx: 1000 });
}
